import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES, T } from "../data/content";
import { useLang } from "../context/LangContext";

const Reveal = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const Manifesto = () => {
  const { tr } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="story" className="relative bg-[#0C0806] py-24 lg:py-36" data-testid="manifesto-section">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="mb-6 flex items-center gap-3">
                <span className="h-px w-10 bg-[#D4AF37]" />
                <span className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37]">{tr(T.manifesto.label)}</span>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl leading-[1.05] tracking-tight text-[#F5ECDA] md:text-5xl lg:text-6xl">
                {tr(T.manifesto.title)}
              </h2>
            </Reveal>

            <div className="mt-14 flex flex-col">
              {T.manifesto.chapters.map((c, i) => (
                <Reveal key={c.num} delay={0.15 + i * 0.1}>
                  <div className="grid grid-cols-[auto_1fr] gap-6 border-t border-white/10 py-8 md:gap-10">
                    <span className="font-serif text-3xl text-[#D4AF37]/80 md:text-4xl">{c.num}</span>
                    <div>
                      <h3 className="mb-3 font-serif text-2xl text-[#F5ECDA] md:text-3xl">{tr(c.title)}</h3>
                      <p className="max-w-xl text-base leading-relaxed text-[#B7A78C]">{tr(c.body)}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative h-[420px] overflow-hidden rounded-sm lg:sticky lg:top-28 lg:h-[640px]">
              <motion.img
                style={{ y }}
                src={IMAGES.founder}
                alt="Essi Delali Miawotoe, founder of Slaly Hair & Beauty"
                className="absolute inset-0 h-[125%] w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="font-serif text-xl italic text-[#F5ECDA]">"{tr({ en: "Every crown deserves care.", fr: "Chaque couronne mérite du soin." })}"</p>
                <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">ESSI · {tr({ en: "Founder", fr: "Fondatrice" })}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
